import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button, Drawer, Input, Select, Space, Table, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ImportOutlined, EditOutlined, InboxOutlined, CheckOutlined } from '@ant-design/icons';
import { listReviewQueue, getReviewItem, dispositionReviewItem } from '@/api/migration';
import { normalizeApiError, isNotAvailable } from '@/api/errors';
import type { ReviewItem, ReviewItemDetail, ReviewStatus } from '@/api/types';
import { parseFlowYaml } from '@/flow-ir/parse';
import { toRenderGraph } from '@/flow-ir/toReactFlow';
import { DesignerCanvas } from '@/designer/DesignerCanvas';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { PageHeader } from '@/components/PageHeader';
import { TableSkeleton } from '@/components/TableSkeleton';
import { usePageTitle } from '@/components/pageTitle';
import { InfoTip } from '@/help/InfoTip';
import { useAppSelector } from '@/store/hooks';

const { Text } = Typography;

/** The four migration reason codes (with corpus counts for the filter hints). */
const REASONS: { code: string; label: string; count: number }[] = [
  { code: 'EXTERNAL_CATALOG_UNRESOLVED', label: 'External catalog unresolved', count: 93 },
  { code: 'DEMO_ESTATE_TEMPLATE_CANDIDATE', label: 'Demo-estate template candidate', count: 92 },
  { code: 'EXTERNAL_APPROVAL_UNRESOLVED', label: 'External approval unresolved', count: 15 },
  { code: 'EXTERNAL_CHAIN_UNRESOLVED', label: 'External chain unresolved', count: 6 },
];
const REASON_LABEL = new Map(REASONS.map((r) => [r.code, r.label]));

const TAB_STATUS: Record<string, ReviewStatus> = {
  pending: 'PENDING',
  accepted: 'ACCEPTED',
  archived: 'ARCHIVED',
};

function ReasonChips({ codes }: { codes: string[] }) {
  return (
    <Space size={4} wrap>
      {codes.map((c) => (
        <Tag
          key={c}
          bordered={false}
          color={c === 'EXTERNAL_CHAIN_UNRESOLVED' ? 'volcano' : c === 'DEMO_ESTATE_TEMPLATE_CANDIDATE' ? 'geekblue' : 'gold'}
          style={{ fontSize: 10.5 }}
          title={REASON_LABEL.get(c) ?? c}
        >
          {c}
        </Tag>
      ))}
    </Space>
  );
}

/** Read-only mini-canvas preview of a translated IR, or a structured JSON fallback. */
function IrPreview({ irPreview }: { irPreview?: string | Record<string, unknown> | null }) {
  const theme = useAppSelector((s) => s.ui.theme);
  // The server sends the canonical IR as a JSON OBJECT (MigrationReviewService — the
  // TemplateDetail convention); older shapes were YAML text. Normalise to text ONCE: an
  // object rendered as a React child unmounts the whole page (minified React #31).
  const irText = useMemo(() => {
    if (!irPreview) return null;
    return typeof irPreview === 'string' ? irPreview : JSON.stringify(irPreview, null, 2);
  }, [irPreview]);
  const parsed = useMemo(() => {
    if (!irText) return null;
    try {
      return parseFlowYaml(irText); // YAML is a JSON superset — one parser serves both shapes
    } catch {
      return null;
    }
  }, [irText]);

  const graph = useMemo(
    () => (parsed ? toRenderGraph(parsed, { dataLayerVisible: false, editable: false }) : null),
    [parsed],
  );

  if (!irText) return <Text type="secondary">No IR preview available.</Text>;
  if (!graph) {
    return (
      <pre
        className="ol-mono"
        style={{
          fontSize: 11,
          background: 'var(--ol-bg-base)',
          border: '1px solid var(--ol-border)',
          borderRadius: 6,
          padding: 10,
          maxHeight: 260,
          overflow: 'auto',
        }}
      >
        {irText}
      </pre>
    );
  }
  return (
    <div style={{ height: 280, border: '1px solid var(--ol-border)', borderRadius: 8, overflow: 'hidden' }}>
      <DesignerCanvas
        nodes={graph.nodes}
        edges={graph.edges}
        layout={graph.layout}
        colorMode={theme}
        editable={false}
        placing={false}
        onSelectNode={() => {}}
        onSelectEdge={() => {}}
      />
    </div>
  );
}

export function MigrationReviewPage() {
  usePageTitle('Migration review');
  const navigate = useNavigate();
  const { message } = App.useApp();

  const [tab, setTab] = useState<string>('pending');
  const [reason, setReason] = useState<string | undefined>();
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [detail, setDetail] = useState<ReviewItemDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [archiveNote, setArchiveNote] = useState('');
  const [acting, setActing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    setError(null);
    try {
      const q = await listReviewQueue({ status: TAB_STATUS[tab], reason, page, pageSize });
      setItems(q.items);
      setTotal(q.totalCount);
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else setError(n.message);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [tab, reason, page, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  const openDetail = useCallback(async (itemId: string) => {
    setDetailLoading(true);
    setArchiveNote('');
    try {
      setDetail(await getReviewItem(itemId));
    } catch (e) {
      message.error(normalizeApiError(e).message);
    } finally {
      setDetailLoading(false);
    }
    // message is stable from App context
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disposition = useCallback(
    async (item: ReviewItem, action: 'ACCEPT_AS_IS' | 'OPEN_IN_DESIGNER' | 'ARCHIVE') => {
      setActing(action);
      try {
        const res = await dispositionReviewItem(item.itemId, action, action === 'ARCHIVE' ? archiveNote || undefined : undefined);
        if (action === 'OPEN_IN_DESIGNER' && res.flowId) {
          navigate(`/flows/${res.flowId}`);
        } else if (action === 'ACCEPT_AS_IS' && res.flowId) {
          const flowId = res.flowId;
          message.success({
            content: (
              <span>
                Accepted — draft created.{' '}
                <a onClick={() => navigate(`/flows/${flowId}`)}>Open in designer</a>
              </span>
            ),
            duration: 6,
          });
          setDetail(null);
          void load();
        } else {
          message.success(`Archived ${item.name}`);
          setDetail(null);
          void load();
        }
      } catch (e) {
        const n = normalizeApiError(e);
        if (isNotAvailable(n)) message.info('The migration disposition endpoint is not live yet (Track A pending).');
        else if (n.code === 'ALREADY_DISPOSITIONED') {
          // Verdicts are final — refresh so the drawer swaps to the terminal-state actions.
          message.info('This item already has its verdict — verdicts are final. Opening the current state.');
          void load();
        } else message.error(n.message);
      } finally {
        setActing(null);
      }
    },
    [archiveNote, navigate, message, load],
  );

  const columns: ColumnsType<ReviewItem> = [
    {
      title: 'Flow',
      dataIndex: 'name',
      key: 'name',
      render: (_, r) => (
        <div style={{ minWidth: 200 }}>
          <div style={{ fontWeight: 600 }}>{r.name}</div>
          <Text className="ol-mono" style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)' }}>
            src: {r.sourceWorkflowId}
          </Text>
        </div>
      ),
    },
    {
      title: 'Translation',
      key: 'translation',
      width: 110,
      render: (_, r) =>
        r.translationStatus ? (
          <Tag
            bordered={false}
            color={r.translationStatus === 'TRANSLATED_CLEAN' ? 'success' : 'warning'}
          >
            {r.translationStatus === 'TRANSLATED_CLEAN' ? 'CLEAN' : 'REVIEW'}
          </Tag>
        ) : (
          <Text style={{ color: 'var(--ol-text-tertiary)' }}>—</Text>
        ),
    },
    { title: 'Reason codes', key: 'reasons', render: (_, r) => <ReasonChips codes={r.reasonCodes} /> },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (s: ReviewStatus) => (
        <Tag
          bordered={false}
          color={s === 'ACCEPTED' ? 'success' : s === 'ARCHIVED' ? 'default' : s === 'IN_DESIGNER' ? 'processing' : 'warning'}
        >
          {s}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'go',
      width: 120,
      align: 'right',
      render: (_, r) => (
        <Button size="small" onClick={() => openDetail(r.itemId)}>
          Review
        </Button>
      ),
    },
  ];

  const table = unavailable ? (
    <EmptyState
      icon={<ImportOutlined />}
      title="Migration review queue"
      description="The 153 flows flagged NEEDS_HUMAN_REVIEW during corpus migration land here once the migration review API is live (Track A, PS-2). This surface unblocks real client-migration work."
      bullets={[
        'EXTERNAL_CATALOG_UNRESOLVED · 93 — capability refs to resolve against the registry',
        'DEMO_ESTATE_TEMPLATE_CANDIDATE · 92 — demo-estate flows to promote as templates',
        'EXTERNAL_APPROVAL_UNRESOLVED · 15 — approval chains to re-home',
        'EXTERNAL_CHAIN_UNRESOLVED · 6 — cross-flow chains to reconnect',
      ]}
    />
  ) : (
    <Table<ReviewItem>
      rowKey="itemId"
      loading={loading && items.length > 0}
      locale={{
        emptyText: loading ? (
          <TableSkeleton label="Loading the review queue" />
        ) : error ? (
          <ErrorState what="the migration review queue" detail={error} onRetry={() => void load()} compact />
        ) : (
          <EmptyState
            compact
            icon={<ImportOutlined />}
            title={tab === 'pending' ? 'Nothing left to review' : `Nothing ${tab}`}
            description={
              tab === 'pending'
                ? 'Every flagged translation under these filters has been dispositioned. New items appear only when another legacy corpus is migrated.'
                : 'No item has reached this state yet. Disposition a pending item — accept it, finish it in the designer, or archive it with a note — and it lands here.'
            }
          />
        ),
      }}
      columns={columns}
      dataSource={items}
      size="small"
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        onChange: (p, ps) => {
          setPage(p);
          setPageSize(ps);
        },
      }}
      onRow={(r) => ({ onClick: () => openDetail(r.itemId), style: { cursor: 'pointer' } })}
    />
  );

  return (
    <div className="ol-page">
      <PageHeader
        title="Migration review"
        icon={<ImportOutlined />}
        subtitle="The 153 legacy flows flagged for human review during corpus migration. Triage each: accept the translation as-is, open it in the designer to finish it, or archive it with a note."
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <Tabs
          activeKey={tab}
          onChange={(k) => {
            setTab(k);
            setPage(1);
          }}
          items={[
            { key: 'pending', label: 'Pending' },
            { key: 'accepted', label: 'Accepted' },
            { key: 'archived', label: 'Archived' },
          ]}
          style={{ flex: 1 }}
        />
        <Select
          allowClear
          placeholder="Filter by reason code"
          style={{ width: 300 }}
          value={reason}
          onChange={(v) => {
            setReason(v);
            setPage(1);
          }}
          options={REASONS.map((r) => ({ value: r.code, label: `${r.code} (${r.count})` }))}
        />
        <InfoTip surface="migration-review" field="Filter by reason code" />
      </div>

      {table}

      <Drawer
        open={Boolean(detail) || detailLoading}
        onClose={() => setDetail(null)}
        width={620}
        title={detail ? detail.name : 'Loading…'}
        loading={detailLoading}
      >
        {detail && (
          <div>
            <Space wrap style={{ marginBottom: 12 }}>
              <Tag bordered={false} className="ol-mono">
                src: {detail.sourceWorkflowId}
              </Tag>
              <Tag bordered={false} color={detail.status === 'ACCEPTED' ? 'success' : 'warning'}>
                {detail.status}
              </Tag>
            </Space>
            <div style={{ marginBottom: 12 }}>
              <ReasonChips codes={detail.reasonCodes} />
            </div>

            {detail.diagnostics && detail.diagnostics.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <Text strong style={{ fontSize: 12 }}>
                  Diagnostics
                </Text>
                {detail.diagnostics.map((dg, i) => (
                  <div key={i} className="ol-problem">
                    <span
                      style={{
                        color: `var(--ol-${dg.severity === 'ERROR' ? 'negative' : dg.severity === 'WARNING' ? 'attention' : 'info'})`,
                      }}
                    >
                      {dg.severity === 'ERROR' ? '●' : dg.severity === 'WARNING' ? '▲' : 'ℹ'}
                    </span>
                    <span>
                      <span className="ol-problem__code">{dg.code}</span> {dg.message}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {detail.sourceSummary != null && (
              <div style={{ marginBottom: 14 }}>
                <Text strong style={{ fontSize: 12 }}>
                  Source summary
                </Text>
                <pre
                  className="ol-mono"
                  style={{
                    fontSize: 11,
                    background: 'var(--ol-bg-base)',
                    border: '1px solid var(--ol-border)',
                    borderRadius: 6,
                    padding: 10,
                    maxHeight: 160,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {typeof detail.sourceSummary === 'string'
                    ? detail.sourceSummary
                    : JSON.stringify(detail.sourceSummary, null, 2)}
                </pre>
              </div>
            )}

            <Text strong style={{ fontSize: 12 }}>
              Translated IR preview
            </Text>
            <div style={{ marginTop: 6 }}>
              <IrPreview irPreview={detail.irPreview} />
            </div>

            <div style={{ marginTop: 18, borderTop: '1px solid var(--ol-border)', paddingTop: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--ol-text-tertiary)', marginBottom: 4 }}>
                Archive note <InfoTip surface="migration-review" field="Archive note" />
              </div>
              <Input.TextArea
                placeholder="Archive note (optional)"
                autoSize={{ minRows: 1, maxRows: 3 }}
                value={archiveNote}
                onChange={(e) => setArchiveNote(e.target.value)}
                style={{ marginBottom: 10 }}
              />
              {detail.status === 'PENDING' ? (
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={acting === 'ACCEPT_AS_IS'}
                    onClick={() => disposition(detail, 'ACCEPT_AS_IS')}
                  >
                    Accept as-is
                  </Button>
                  <Button
                    icon={<EditOutlined />}
                    loading={acting === 'OPEN_IN_DESIGNER'}
                    onClick={() => disposition(detail, 'OPEN_IN_DESIGNER')}
                  >
                    Open in designer
                  </Button>
                  <Button
                    danger
                    icon={<InboxOutlined />}
                    loading={acting === 'ARCHIVE'}
                    onClick={() => disposition(detail, 'ARCHIVE')}
                  >
                    Archive
                  </Button>
                </Space>
              ) : (
                // Dispositioned items are TERMINAL (the state machine refuses a second verdict
                // with a 409) — the only sensible action is opening the flow that came out of
                // the verdict. Re-disposition buttons here produced a swallowed 409 + stuck
                // spinner; never again.
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    disabled={!detail.translatedFlowId}
                    onClick={() => navigate(`/flows/${detail.translatedFlowId}`)}
                  >
                    Open in designer
                  </Button>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {detail.status === 'ARCHIVED'
                      ? 'Archived — recover it via the Templates gallery (estate candidates).'
                      : `Verdict recorded (${detail.status}) — verdicts are final; the flow itself is what you edit now.`}
                  </Text>
                </Space>
              )}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
